(() => {
  const behavior = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  class KsProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      const on = (el, type, fn, options = {}) => el?.addEventListener(type, fn, {signal:this.abort.signal,...options});
      this.data = JSON.parse(this.querySelector('[data-ks-product-data]').textContent);
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-ks-variant-input]');
      this.button = this.querySelector('[data-ks-add]');
      this.variant = this.data.variants.find(v => String(v.id) === this.input.value);
      this.track = this.querySelector('[data-ks-track]');
      this.slides = [...this.querySelectorAll('[data-media-id]')];
      this.thumbnails = [...this.querySelectorAll('[data-ks-thumbnail]')];
      this.index = 0;
      on(this, 'click', e => {
        const variant = e.target.closest('[data-ks-variant]');
        const thumb = e.target.closest('[data-ks-thumbnail]');
        const step = e.target.closest('[data-ks-step]');
        if (variant) this.selectVariant(variant.dataset.ksVariant, true);
        if (thumb) this.goToMedia(thumb.dataset.ksThumbnail);
        if (step) this.goToIndex(this.index + Number(step.dataset.ksStep));
      });
      on(this.track, 'scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.syncGallery());
      }, {passive:true});
      on(this.querySelector('[data-ks-size-guide]'), 'click', e => {
        const expanded = e.currentTarget.getAttribute('aria-expanded') === 'true';
        e.currentTarget.setAttribute('aria-expanded', String(!expanded));
        this.querySelector('[data-ks-compare]').hidden = expanded;
      });
      on(this.form, 'submit', e => this.addToCart(e), {capture:true});
      on(window, 'popstate', () => this.selectVariant(new URL(location.href).searchParams.get('variant') || this.data.variants[0].id, false));
      this.resize = new ResizeObserver(() => this.goToIndex(this.index, 'instant'));
      this.resize.observe(this.track);
      this.updateShipping();
      requestAnimationFrame(() => this.goToMedia(this.querySelector('[data-ks-gallery]').dataset.initialMedia, 'instant'));
      const country = this.dataset.country;
      if (/^[A-Z]{2}$/.test(country) && this.querySelector('[data-ks-country-flag]')) this.querySelector('[data-ks-country-flag]').textContent = [...country].map(c => String.fromCodePoint(c.charCodeAt(0) + 127397)).join('');
      this.querySelectorAll('[data-ks-addon]').forEach(addon => addon.removeAttribute('disabled'));
      this.setupSticky();
      const badge = this.querySelector('.jdgm-prev-badge');
      const rating = Number(badge?.dataset.averageRating);
      if (rating > 0 && !badge.querySelector('.ks-pdp-review-score')) {
        const score = document.createElement('strong');
        score.className = 'ks-pdp-review-score';
        score.textContent = rating.toFixed(1);
        badge.querySelector('.jdgm-prev-badge__stars')?.after(score);
      }
      const reviews = document.getElementById('judgeme_product_reviews');
      if (reviews && Number.isFinite(rating) && rating > 0) {
        const display = rating.toFixed(1);
        const updateAverage = () => reviews.querySelectorAll('.jm-average-rating-display > .jm-cluster > .jm-text:first-of-type, .jdgm-rev-widg__summary-average').forEach(node => {
          const text = node.textContent.replace(/^\s*\d+(?:[.,]\d+)?/, display);
          if (node.textContent !== text) node.textContent = text;
        });
        updateAverage();
        this.ratingObserver = new MutationObserver(updateAverage);
        this.ratingObserver.observe(reviews, {childList:true,subtree:true,characterData:true});
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.stickyObserver?.disconnect();
      this.stickySize?.disconnect();
      this.ratingObserver?.disconnect();
      document.body.classList.remove('ks-pdp-sticky-visible');
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.stickyFrame);
      this.initialized = false;
    }
    setupSticky() {
      this.sticky = this.querySelector('[data-ks-sticky]');
      if (!this.sticky) return;
      this.stickySelect = this.sticky.querySelector('[data-ks-sticky-variant]');
      this.stickyButton = this.sticky.querySelector('[data-ks-sticky-add]');
      const reviewLink = this.sticky.querySelector('[data-ks-sticky-reviews]');
      const badge = this.querySelector('.ks-pdp-rating .jdgm-prev-badge');
      const average = Number(badge?.dataset.averageRating);
      const count = Number(badge?.dataset.numberOfReviews);
      if (reviewLink && average > 0 && count > 0 && document.getElementById('judgeme_product_reviews')) {
        this.sticky.querySelector('[data-ks-sticky-rating]').textContent = `${average.toFixed(1)} (${count.toLocaleString()})`;
        const shortRating = this.sticky.querySelector('[data-ks-sticky-rating-short]');
        if (shortRating) shortRating.textContent = average.toFixed(1);
        reviewLink.hidden = false;
      }
      const options = {signal:this.abort.signal};
      this.stickySelect.addEventListener('change', () => this.selectVariant(this.stickySelect.value, true), options);
      this.stickyButton.addEventListener('click', () => {
        if (!this.pending && this.variant?.available) this.form.requestSubmit(this.button);
      }, options);
      const resize = () => {
        if (!this.sticky.hidden) document.body.style.setProperty('--ks-sticky-height', this.sticky.offsetHeight + 'px');
      };
      this.stickySize = new ResizeObserver(resize);
      this.stickySize.observe(this.sticky);
      const updateVisibility = () => {
        const hidden = this.button.getBoundingClientRect().bottom > 0;
        if (this.sticky.hidden !== hidden) {
          this.sticky.hidden = hidden;
          document.body.classList.toggle('ks-pdp-sticky-visible', !hidden);
          resize();
        }
      };
      const scheduleVisibility = () => {
        cancelAnimationFrame(this.stickyFrame);
        this.stickyFrame = requestAnimationFrame(updateVisibility);
      };
      // A fast scroll can skip the main button without changing its intersection state.
      window.addEventListener('scroll', scheduleVisibility, {...options, passive:true});
      window.addEventListener('resize', scheduleVisibility, options);
      this.stickyObserver = new IntersectionObserver(updateVisibility);
      this.stickyObserver.observe(this.button);
      updateVisibility();
      this.syncSticky();
    }
    syncSticky() {
      if (!this.sticky || !this.variant) return;
      this.stickySelect.value = String(this.variant.id);
      this.stickySelect.disabled = Boolean(this.pending);
      this.sticky.querySelector('[data-ks-sticky-price]').textContent = this.variant.price;
      this.stickyButton.disabled = this.button.disabled;
      this.stickyButton.textContent = this.button.textContent;
      const status = this.querySelector('[data-ks-status]');
      const stickyStatus = this.sticky.querySelector('[data-ks-sticky-status]');
      stickyStatus.hidden = status.hidden || status.dataset.error !== 'true';
      stickyStatus.textContent = stickyStatus.hidden ? '' : status.textContent;
    }
    updateShipping() {
      if (!this.variant) return;
      // Shopify supplies the active market's exchange rate on each page load.
      // Prices and thresholds use Shopify's currency subunits, including JPY.
      const active = window.Shopify?.currency;
      const rate = this.data.currency === 'USD' ? 1 :
        this.data.shopCurrency === 'USD' && active?.active === this.data.currency ? Number(active.rate) : 0;
      const threshold = Number.isFinite(rate) && rate > 0 ? Math.round(10000 * rate) : null;
      const eligible = threshold !== null && this.variant.priceAmount >= threshold;
      const badge = this.querySelector('[data-ks-free-shipping]');
      if (badge) badge.hidden = !eligible;
      if (this.data.customShipping) return;
      const amount = threshold === null ? 'US$100' : new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style:'currency',currency:this.data.currency,currencyDisplay:'narrowSymbol',minimumFractionDigits:0,maximumFractionDigits:2
      }).format(threshold / 100);
      this.querySelectorAll('[data-ks-assurance-shipping-title]').forEach(el => {
        el.textContent = this.data.strings.freeShipping;
      });
      this.querySelectorAll('[data-ks-assurance-shipping-detail]').forEach(el => {
        el.textContent = eligible ? this.data.strings.shippingIncluded : amount + '+';
      });
      this.querySelectorAll('[data-ks-shipping]').forEach(el => {
        el.textContent = eligible ? this.data.strings.freeShipping : this.data.strings.shippingMinimum.replace('__AMOUNT__',amount);
      });
    }
    goToMedia(id, mode = 'instant') {
      const index = this.slides.findIndex(s => s.dataset.mediaId === String(id));
      if (index >= 0) this.goToIndex(index, mode);
    }
    goToIndex(index, mode = behavior()) {
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({left:this.index * this.track.clientWidth, behavior:mode});
      this.syncGallery();
    }
    playLeadVideo(video) {
      if (!video || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      video.muted = true;
      video.play().catch(() => {});
    }
    updateLeadVideo(lead) {
      const video = this.querySelector('[data-ks-lead-video]');
      if (!lead || !video) return false;
      const changed = video.getAttribute('src') !== lead.src;
      if (changed) {
        video.pause();
        video.src = lead.src;
        video.poster = lead.poster + '&width=960';
        video.setAttribute('aria-label', lead.title);
        const thumb = this.querySelector('[data-ks-thumbnail="motion"]');
        thumb?.setAttribute('aria-label', lead.title);
        const image = thumb?.querySelector('img');
        if (image) image.src = lead.poster + '&width=160';
      }
      this.goToMedia('motion');
      if (changed) this.playLeadVideo(video);
      return true;
    }
    syncGallery() {
      const width = this.track.clientWidth;
      if (!width) return;
      this.index = Math.round(this.track.scrollLeft / width);
      this.thumbnails.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === this.index)));
      const counter = this.querySelector('[data-ks-counter]');
      if (counter) counter.textContent = `${this.index + 1} / ${this.slides.length}`;
      const prev = this.querySelector('[data-ks-step="-1"]');
      const next = this.querySelector('[data-ks-step="1"]');
      if (prev) prev.disabled = this.index === 0;
      if (next) next.disabled = this.index === this.slides.length - 1;
      const thumb = this.thumbnails[this.index];
      if (thumb) {
        const rail = thumb.parentElement;
        const left = thumb.offsetLeft - rail.offsetLeft;
        if (left < rail.scrollLeft || left + thumb.offsetWidth > rail.scrollLeft + rail.clientWidth) rail.scrollTo({left:Math.max(0,left - rail.clientWidth / 2 + thumb.offsetWidth / 2),behavior:behavior()});
      }
      this.slides.forEach((slide, i) => {if (i !== this.index) slide.querySelector('video')?.pause();});
      const active = this.slides[this.index];
      if (active?.dataset.mediaId !== this.activeMediaId) {
        this.activeMediaId = active?.dataset.mediaId;
        this.playLeadVideo(active?.querySelector(active?.dataset.ksAutoplay === 'true' ? 'video' : '[data-ks-lead-video]'));
      }
    }
    selectVariant(id, updateUrl) {
      const v = this.data.variants.find(v => String(v.id) === String(id));
      if (!v || this.pending) return;
      this.variant = v;
      this.input.value = v.id;
      this.input.dispatchEvent(new Event('change', {bubbles:true}));
      this.querySelectorAll('[data-ks-variant]').forEach(b => b.setAttribute('aria-pressed',String(b.dataset.ksVariant === String(v.id))));
      const setDetail = this.querySelector('[data-ks-set-detail]');
      if (setDetail) setDetail.textContent = this.querySelector('.ks-w-options--sets [aria-pressed="true"] .ks-choice-caption')?.textContent.trim() || '';
      const modelName = this.querySelector('[data-ks-model-name]');
      if (modelName) modelName.textContent = v.title;
      this.querySelector('[data-ks-price]').textContent = v.price;
      this.updateShipping();
      const compare = this.querySelector('[data-ks-compare-price]');
      compare.hidden = !v.comparePrice;
      compare.textContent = v.comparePrice || '';
      const grip = this.querySelector('[data-ks-grip-benefit]');
      if (grip && v.gripBenefit) grip.textContent = v.gripBenefit;
      this.querySelector('[data-ks-stock]').dataset.available = v.available;
      this.querySelector('[data-ks-stock]').dataset.backorder = v.backorder;
      this.querySelector('[data-ks-stock-text]').textContent = !v.available ? this.data.strings.soldOut : v.backorder ? this.data.strings.backorder : this.data.strings.inStock;
      this.querySelector('[data-ks-delivery]').hidden = !v.available || v.backorder || !this.data.showDelivery;
      const preorderMessage = this.querySelector('[data-ks-backorder-message]');
      if (preorderMessage) preorderMessage.hidden = !v.available || !v.backorder;
      this.button.disabled = !v.available;
      this.button.textContent = !v.available ? this.data.strings.soldOut : v.backorder ? this.data.strings.backorderAdd : this.data.strings.add;
      this.querySelector('[data-ks-status]').hidden = true;
      if (!this.updateLeadVideo(v.leadVideo) && v.mediaId) this.goToMedia(v.mediaId);
      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant',v.id);
        history.replaceState({},'',url);
      }
      this.syncSticky();
      this.dispatchEvent(new CustomEvent('variant:change',{bubbles:true,detail:{variant:v}}));
    }
    async addToCart(e) {
      if (e.submitter?.closest('.ks-express-checkout')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (this.pending || !this.variant.available) return;
      this.pending = true;
      this.button.disabled = true;
      this.button.textContent = this.data.strings.adding;
      this.form.setAttribute('aria-busy','true');
      const status = this.querySelector('[data-ks-status]');
      status.hidden = true;
      this.syncSticky();
      const items = [{id:this.variant.id,quantity:1}];
      this.querySelectorAll('[data-ks-addon]:checked:not(:disabled)').forEach(addon => items.push({id:Number(addon.dataset.variantId),quantity:Math.max(1,Number.parseInt(addon.dataset.quantity,10)||1)}));
      try {
        const response = await fetch(this.data.addUrl,{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items})});
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : this.data.strings.error);
        status.dataset.error = 'false';
        status.textContent = this.data.strings.added + ' ';
        const link = document.createElement('a');
        link.href = this.data.cartUrl;
        link.textContent = this.data.strings.cart;
        status.append(link);
        document.dispatchEvent(new CustomEvent('cart:open'));
      } catch (error) {
        status.dataset.error = 'true';
        status.textContent = error instanceof TypeError ? this.data.strings.error : error.message;
      } finally {
        status.hidden = false;
        this.pending = false;
        this.form.removeAttribute('aria-busy');
        this.button.disabled = !this.variant.available;
        this.button.textContent = !this.variant.available ? this.data.strings.soldOut : this.variant.backorder ? this.data.strings.backorderAdd : this.data.strings.add;
        this.syncSticky();
      }
    }
  }
  class KsPractice extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      const tabs = [...this.querySelectorAll('[role=tab]')];
      const select = index => {
        tabs.forEach((tab,i) => {tab.setAttribute('aria-selected',String(i === index));tab.tabIndex = i === index ? 0 : -1;});
        this.querySelectorAll('[role=tabpanel]').forEach((panel,i) => panel.hidden = i !== index);
      };
      tabs.forEach((tab,i) => {
        tab.addEventListener('click',()=>select(i),{signal:this.abort.signal});
        tab.addEventListener('keydown',e=>{
          let index;
          if (e.key === 'ArrowRight') index = (i+1)%tabs.length;
          if (e.key === 'ArrowLeft') index = (i-1+tabs.length)%tabs.length;
          if (e.key === 'Home') index = 0;
          if (e.key === 'End') index = tabs.length-1;
          if (index !== undefined) {e.preventDefault();select(index);tabs[index].focus();}
        },{signal:this.abort.signal});
      });
    }
    disconnectedCallback() {this.abort?.abort();this.initialized = false;}
  }
  if (!customElements.get('ks-product')) customElements.define('ks-product',KsProduct);
  if (!customElements.get('ks-practice')) customElements.define('ks-practice',KsPractice);
})();



